import React from "react";
import { Link, Redirect } from "react-router-dom";
import useStore from "../../../context/useStore";
import { sidePanelActions } from "../../../context/actions/sidePanelActions";
import tools from "../../../helpers/tools";
import { estAnonymise } from "../../../helpers/allocataire";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  DateCell,
  ListFilters,
  ListPageHeader,
  ListSearchForm,
  Pill,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import DetailDemandeSuspensionRemobilisation from "./DetailDemandeSuspensionRemobilisation";
import { STATUT_TONES, demandeur, nomComplet } from "./suspensionRemobilisationLabels";
import {
  COUNT_LABELS,
  COUNT_LABELS_A_VALIDER,
  DEMANDES_DEFAULT_FILTERS,
  DEMANDES_ENDPOINT,
  DEMANDES_FILTER_DEFINITIONS,
  DEMANDES_FILTER_GROUPS,
  buildDemandesPayload,
  cibleDemande,
  demandesAtoms,
} from "./suspensionRemobilisationListConfig";
import "./suspension-remobilisation.scss";

export { cibleDemande } from "./suspensionRemobilisationListConfig";

export const estDirecteurOuDelegataire = (user) =>
  !!user && (user.role === "CAF_DIRECTOR" || user.isDirector === true);

export const FILTERS_PANEL_ID = "suspension-remobilisation-filters";
const FILTERS_OPEN_KEY = "espoar.boa.suspensionRemobilisation.filtersOpen";

const LienAllocataire = ({ recipient }) => (
  <>
    {recipient ? (
      <Link
        to={tools.getRecipientLinkFromParams(recipient.type, recipient.id)}
        className="recipient-name"
        target="_blank"
        rel="noopener noreferrer"
      >
        {nomComplet(recipient)}
      </Link>
    ) : (
      <span className="text-muted">Non rattaché</span>
    )}
    {estAnonymise(recipient) && (
      <Pill tone="muted" icon="fa-user-secret" className="ml-2">
        Anonymisé
      </Pill>
    )}
  </>
);

// Sept colonnes : la demande et ce qu'elle vise se lisent ensemble, dans une
// même cellule ; l'origine se lit sous le demandeur.
export const getDemandesColumns = ({ ouvrir }) => [
  {
    key: "requestedAt",
    header: "Demandée le",
    className: "col-date",
    render: (d) => <DateCell value={d.requestedAt} />,
  },
  {
    key: "recipient",
    header: "Allocataire",
    className: "col-recipient",
    render: (d) => <LienAllocataire recipient={d.recipient} />,
  },
  {
    key: "demande",
    header: "Demande",
    render: (d) => (
      <>
        <span className="cell-primary">
          {d.outcomeLabel || <span className="text-muted">À qualifier</span>}
        </span>
        <span className="cell-secondary">{cibleDemande(d)}</span>
      </>
    ),
  },
  {
    key: "motif",
    header: "Motif",
    render: (d) => d.reasonForLiftingLabel || d.partnerJustification || <span className="text-muted">—</span>,
  },
  {
    key: "demandeur",
    header: "Demandée par",
    render: (d) => (
      <>
        {demandeur(d) || <span className="text-muted">—</span>}
        {d.originLabel && d.requestedBy && <span className="cell-secondary">{d.originLabel}</span>}
      </>
    ),
  },
  {
    key: "etat",
    header: "État",
    render: (d) => (
      <>
        <Pill tone={STATUT_TONES[d.status] ?? "neutral"}>{d.statusLabel}</Pill>
        {d.epRegistrationWithdrawal && (
          <span className="cell-secondary">
            {d.epRegistrationWithdrawal.done ? "Retirée de la commission" : "Non retirée de la commission"}
          </span>
        )}
      </>
    ),
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (d) => (
      <button
        type="button"
        className="btn btn-outline-primary btn-icon suspension-remobilisation__voir"
        onClick={() => ouvrir(d)}
        aria-label={`Voir la demande${d.recipient ? ` de ${nomComplet(d.recipient)}` : ""}`}
        title="Voir la demande"
      >
        <i className="fa fa-eye" aria-hidden="true" />
      </button>
    ),
  },
];

// Page de gestion des demandes de Suspension / Remobilisation : liste du kit
// DataList, comme les allocataires. Toute demande — remobilisation ou levée
// de saisine, saisie par le référent unique ou venue de France Travail — y
// attend la décision du Directeur de la Caf ou de son délégataire, et y reste
// consultable ensuite.
const DemandesSuspensionRemobilisation = () => {
  const [state, dispatch] = useStore();
  const spActions = sidePanelActions(state, dispatch);
  const [filtersOpen, setFiltersOpen] = usePersistedState(FILTERS_OPEN_KEY, true);
  const autorise = estDirecteurOuDelegataire(state.auth?.user);

  const list = useListPage({
    searchAtom: demandesAtoms.searchAtom,
    resultsAtom: demandesAtoms.resultsAtom,
    defaultFilters: DEMANDES_DEFAULT_FILTERS,
    endpoint: DEMANDES_ENDPOINT,
    buildPayload: buildDemandesPayload,
    definitions: DEMANDES_FILTER_DEFINITIONS,
    groups: DEMANDES_FILTER_GROUPS,
    // Un agent non habilité est renvoyé à l'accueil : pas de requête pour rien.
    autoSearch: autorise,
  });

  if (!autorise) {
    return <Redirect to="/" />;
  }

  const fermerPanneau = () => spActions.updateSidePanel({ isOpen: false, content: null });
  const ouvrir = (demande) =>
    spActions.updateSidePanel({
      isOpen: true,
      content: (
        <DetailDemandeSuspensionRemobilisation
          demande={demande}
          onDone={() => {
            fermerPanneau();
            list.runSearch(list.search);
          }}
        />
      ),
    });

  const statutApplique = list.results.appliedFilters
    ? list.results.appliedFilters.status
    : list.filters.status;
  const compteur = formatResultCount(
    list.count,
    statutApplique === "EN_ATTENTE_VALIDATION" ? COUNT_LABELS_A_VALIDER : COUNT_LABELS
  );
  const columns = getDemandesColumns({ ouvrir });

  return (
    <div className="container-fluid px-5 my-5 list-page suspension-remobilisation">
      <ListPageHeader
        title="Suspension / Remobilisation"
        subtitle="Demandes de remobilisation et de levée de saisine soumises au Directeur de la Caf."
      />

      <ListSearchForm onSubmit={list.submit} aria-label="Recherche de demandes">
        <SearchToolbar
          id="suspension-remobilisation-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="Nom, prénom, NIR ou n° allocataire"
          isLoading={list.isLoading}
          isDirty={list.isDirty}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          inlineFiltersLayout="ligne"
          filtersLabel="Plus de filtres"
          filtersPanelId={FILTERS_PANEL_ID}
          filtersOpen={filtersOpen}
          onToggleFilters={list.hasPanelFilters ? () => setFiltersOpen((open) => !open) : null}
          activeFiltersCount={list.panelActiveFiltersCount}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
        {list.hasPanelFilters && (
          <ListFilters
            id={FILTERS_PANEL_ID}
            open={filtersOpen}
            groups={list.groups}
            filters={list.filters}
            onChange={list.onFilterChange}
            onSet={list.setFilter}
            onReset={list.resetFilters}
            canReset={list.canReset}
          />
        )}
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {compteur}
          </p>
        </div>
        <DataTable
          caption="Demandes de Suspension / Remobilisation"
          columns={columns}
          rows={list.items}
          rowKey="id"
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          emptyState={{
            title: "Aucune demande ne correspond à ces critères",
            hint:
              statutApplique === "EN_ATTENTE_VALIDATION"
                ? "Rien à valider pour l'instant. Choisissez « Tous » dans le statut pour voir l'historique."
                : "Modifiez votre recherche ou vos filtres.",
            action: list.canReset
              ? { label: "Réinitialiser les filtres", onClick: list.resetFilters }
              : null,
          }}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="demandes"
        />
      </section>
    </div>
  );
};

export default DemandesSuspensionRemobilisation;
